import { useState, useEffect, type FormEvent } from 'react';
import {
  multiFactor,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  TotpMultiFactorGenerator,
  RecaptchaVerifier,
  type TotpSecret,
} from 'firebase/auth';
import QRCode from 'qrcode';
import { auth } from '../../lib/firebase.js';
import { useAuth } from '../../lib/auth.js';

const RECAPTCHA_CONTAINER_ID = 'sfp-mfa-recaptcha';

type Method = 'choose' | 'totp' | 'sms';

/**
 * Two-factor enrollment — offers an authenticator app (TOTP, recommended) or
 * text message (SMS). Lives at /settings/mfa AND auto-renders as a blocking
 * modal on a signed-in user's first visit (MFA is mandatory).
 */
export function MfaEnroll({
  mode = 'page',
  onDone,
}: {
  mode?: 'page' | 'modal';
  onDone?: () => void;
}) {
  const { user } = useAuth();
  const [method, setMethod] = useState<Method>('choose');
  const [phone, setPhone] = useState('');
  const [verificationId, setVerificationId] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [verifier, setVerifier] = useState<RecaptchaVerifier | null>(null);
  const [totpSecret, setTotpSecret] = useState<TotpSecret | null>(null);
  const [qrUrl, setQrUrl] = useState<string | null>(null);

  useEffect(() => {
    const v = new RecaptchaVerifier(auth, RECAPTCHA_CONTAINER_ID, { size: 'invisible' });
    setVerifier(v);
    return () => {
      try {
        v.clear();
      } catch {
        /* ignore */
      }
    };
  }, []);

  // ---- TOTP (authenticator app) --------------------------------------------
  async function startTotp() {
    if (!user) return;
    setErr(null);
    setBusy(true);
    try {
      const session = await multiFactor(user).getSession();
      const secret = await TotpMultiFactorGenerator.generateSecret(session);
      setTotpSecret(secret);
      const url = secret.generateQrCodeUrl(user.email ?? 'SFP', 'SFP — Shulkin Family Partners');
      setQrUrl(await QRCode.toDataURL(url, { margin: 1, width: 208 }));
      setMethod('totp');
    } catch (e2) {
      setErr(prettify(e2));
    } finally {
      setBusy(false);
    }
  }

  async function confirmTotp(e: FormEvent) {
    e.preventDefault();
    if (!user || !totpSecret) return;
    setErr(null);
    setBusy(true);
    try {
      const assertion = TotpMultiFactorGenerator.assertionForEnrollment(totpSecret, code);
      await multiFactor(user).enroll(assertion, 'Authenticator app');
      setDone(true);
      await user.getIdToken(true);
      onDone?.();
    } catch (e2) {
      setErr(prettify(e2));
    } finally {
      setBusy(false);
    }
  }

  // ---- SMS ------------------------------------------------------------------
  async function sendCode(e: FormEvent) {
    e.preventDefault();
    if (!user || !verifier) return;
    setErr(null);
    setBusy(true);
    try {
      const session = await multiFactor(user).getSession();
      const id = await new PhoneAuthProvider(auth).verifyPhoneNumber(
        { phoneNumber: toE164(phone), session },
        verifier,
      );
      setVerificationId(id);
    } catch (e2) {
      setErr(prettify(e2));
    } finally {
      setBusy(false);
    }
  }

  async function confirmSms(e: FormEvent) {
    e.preventDefault();
    if (!user || !verificationId) return;
    setErr(null);
    setBusy(true);
    try {
      const cred = PhoneAuthProvider.credential(verificationId, code);
      const assertion = PhoneMultiFactorGenerator.assertion(cred);
      await multiFactor(user).enroll(assertion, 'Mobile');
      setDone(true);
      await user.getIdToken(true);
      onDone?.();
    } catch (e2) {
      setErr(prettify(e2));
    } finally {
      setBusy(false);
    }
  }

  function back() {
    setErr(null);
    setCode('');
    setVerificationId(null);
    setTotpSecret(null);
    setQrUrl(null);
    setMethod('choose');
  }

  const wrapper =
    mode === 'modal'
      ? 'fixed inset-0 z-50 grid place-items-center bg-black/50 px-4'
      : 'mx-auto max-w-md px-6 py-12';
  const card =
    mode === 'modal'
      ? 'w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl'
      : 'rounded-2xl border border-stone-200 bg-white p-6 shadow-sm';

  const title = done
    ? 'Two-factor enabled'
    : method === 'totp'
      ? 'Scan with your authenticator app'
      : method === 'sms'
        ? verificationId
          ? 'Enter the code we texted'
          : 'Set up text-message sign-in'
        : 'Set up two-factor sign-in';

  const primaryBtn =
    'w-full rounded bg-[color:var(--sfp-primary)] py-2 text-sm font-medium text-white hover:bg-[color:var(--sfp-primary-hover)] disabled:opacity-50';

  return (
    <div className={wrapper}>
      <div className={card}>
        <div className="text-[10px] uppercase tracking-wider text-stone-400">Security</div>
        <h1 className="mt-1 text-lg font-semibold text-stone-800">{title}</h1>
        {!done && (
          <p className="mt-1 text-xs leading-relaxed text-stone-500">
            SFP requires a second factor on every sign-in. Choose how you want to verify.
          </p>
        )}

        {done ? (
          <div className="mt-4 space-y-3">
            <div className="rounded border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
              You're enrolled. Every future sign-in will prompt for a one-time code.
            </div>
            {mode === 'modal' && (
              <button onClick={onDone} className={primaryBtn} type="button">
                Continue
              </button>
            )}
          </div>
        ) : method === 'choose' ? (
          <div className="mt-4 space-y-2">
            <button
              type="button"
              onClick={startTotp}
              disabled={busy}
              className="flex w-full items-center gap-3 rounded-lg border border-stone-200 p-3 text-left hover:border-[color:var(--sfp-primary)] disabled:opacity-50"
            >
              <span className="grid h-9 w-9 place-items-center rounded-md bg-stone-100 text-lg">▦</span>
              <span>
                <span className="block text-sm font-medium text-stone-800">
                  Authenticator app <span className="text-[10px] text-[color:var(--sfp-primary)]">· recommended</span>
                </span>
                <span className="block text-[11px] text-stone-500">
                  Google Authenticator, Authy, 1Password — no SMS, works offline.
                </span>
              </span>
            </button>
            <button
              type="button"
              onClick={() => setMethod('sms')}
              className="flex w-full items-center gap-3 rounded-lg border border-stone-200 p-3 text-left hover:border-[color:var(--sfp-primary)]"
            >
              <span className="grid h-9 w-9 place-items-center rounded-md bg-stone-100 text-lg">✉</span>
              <span>
                <span className="block text-sm font-medium text-stone-800">Text message (SMS)</span>
                <span className="block text-[11px] text-stone-500">A code texted to your mobile number.</span>
              </span>
            </button>
            {err && <div className="text-xs text-[#9C4A3A]">{err}</div>}
          </div>
        ) : method === 'totp' ? (
          <form onSubmit={confirmTotp} className="mt-4 space-y-3">
            <p className="text-[11px] leading-relaxed text-stone-500">
              Scan this in your authenticator app, then enter the 6-digit code it shows.
            </p>
            {qrUrl && (
              <div className="flex justify-center">
                <img src={qrUrl} alt="Authenticator QR code" className="rounded-lg border border-stone-200" />
              </div>
            )}
            {totpSecret && (
              <div className="text-center text-[10px] text-stone-400">
                Can't scan? Enter this key manually:
                <br />
                <span className="select-all break-all font-mono text-[11px] text-stone-600">
                  {totpSecret.secretKey}
                </span>
              </div>
            )}
            <input
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
              placeholder="123456"
              className="w-full rounded border border-stone-300 px-3 py-2 text-center font-mono text-lg tracking-widest"
              autoFocus
            />
            {err && <div className="text-xs text-[#9C4A3A]">{err}</div>}
            <button type="submit" disabled={busy || code.length !== 6} className={primaryBtn}>
              {busy ? 'Verifying…' : 'Verify & enable'}
            </button>
            <button type="button" onClick={back} className="block w-full text-center text-[11px] text-stone-500 hover:text-stone-800">
              ← Choose a different method
            </button>
          </form>
        ) : verificationId ? (
          <form onSubmit={confirmSms} className="mt-4 space-y-3">
            <label className="block text-xs">
              <span className="text-stone-500">SMS code</span>
              <input
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                placeholder="123456"
                className="w-full rounded border border-stone-300 px-3 py-2 text-center font-mono text-lg tracking-widest"
                autoFocus
              />
            </label>
            {err && <div className="text-xs text-[#9C4A3A]">{err}</div>}
            <button type="submit" disabled={busy || code.length !== 6} className={primaryBtn}>
              {busy ? 'Confirming…' : 'Confirm code'}
            </button>
            <button type="button" onClick={back} className="block w-full text-center text-[11px] text-stone-500 hover:text-stone-800">
              ← Choose a different method
            </button>
          </form>
        ) : (
          <form onSubmit={sendCode} className="mt-4 space-y-3">
            <label className="block text-xs">
              <span className="text-stone-500">Mobile number</span>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="(512) 555-1234"
                required
                className="w-full rounded border border-stone-300 px-3 py-2 text-sm"
                autoFocus
              />
              <span className="mt-1 block text-[10px] text-stone-400">
                US numbers: just type the 10 digits — we add +1 for you.
                {phone.trim() && (
                  <> Will send to <span className="font-mono text-stone-600">{toE164(phone)}</span>.</>
                )}
              </span>
            </label>
            {err && <div className="text-xs text-[#9C4A3A]">{err}</div>}
            <button type="submit" disabled={busy} className={primaryBtn}>
              {busy ? 'Sending code…' : 'Send code'}
            </button>
            <button type="button" onClick={back} className="block w-full text-center text-[11px] text-stone-500 hover:text-stone-800">
              ← Choose a different method
            </button>
          </form>
        )}
        <div id={RECAPTCHA_CONTAINER_ID} />
      </div>
    </div>
  );
}

/**
 * Normalize a typed phone number to E.164, defaulting to US (+1).
 *   "(512) 555-1234" / "5125551234" / "15125551234" -> "+15125551234"
 *   "+44 20 7..." -> kept as-is (a + was given).
 */
function toE164(raw: string): string {
  const t = raw.trim();
  if (t.startsWith('+')) return '+' + t.slice(1).replace(/\D/g, '');
  const d = t.replace(/\D/g, '');
  if (d.length === 11 && d.startsWith('1')) return '+' + d;
  if (d.length === 10) return '+1' + d;
  return '+' + d;
}

function prettify(err: unknown): string {
  const m = err instanceof Error ? err.message : String(err);
  if (/invalid-phone-number/i.test(m))
    return 'That number is not valid. Enter a 10-digit US mobile number (international: include your country code).';
  if (/invalid-verification-code|invalid-otp|invalid.*code/i.test(m))
    return 'That code is wrong or expired. Check your app/message and try again.';
  if (/code-expired/i.test(m)) return 'That code expired. Start again.';
  if (/requires-recent-login/i.test(m))
    return 'For security, sign in again before enrolling MFA, then retry.';
  if (/too-many-requests/i.test(m)) return 'Too many attempts. Wait a few minutes.';
  if (/missing-app-credential|recaptcha/i.test(m)) return 'reCAPTCHA failed. Refresh the page and retry.';
  if (/totp.*not.*enabled|operation-not-allowed/i.test(m))
    return 'Authenticator-app MFA is not enabled for this project yet.';
  return m.replace(/^Firebase:\s*/, '').replace(/\s+\(auth\/[^)]+\)\.?$/, '');
}
